/**
 * AddToPipelineDialogView — pure presentation for the reusable
 * "pick pipeline + stage" dialog (SE-5499, AC5).
 *
 * Two closed `Select` dropdowns (pipeline board, then stage) plus a confirm
 * button, inside a `Modal`. The board picker is hidden when the dialog is opened
 * with a known board (the future on-board call site — "pick stage only"); the
 * profile call site shows both. Nothing here reaches into open-board context —
 * every value is a prop, which is what makes the dialog reusable across surfaces
 * without a rebuild.
 * @remarks View component — no business logic, hooks, or state.
 */

import { ChevronDownIcon } from "lucide-react-native";
import { memo } from "react";

import { Button } from "@/components/atoms/Button";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** A pipeline board option. */
export interface PipelineBoardOption {
  readonly id: string;
  readonly name: string;
}

/** A stage (phase) option. */
export interface PipelineStageOption {
  readonly id: string;
  readonly name: string;
  /** Terminal stages are labelled so the manager knows an outcome is implied. */
  readonly isTerminal: boolean;
}

/** Props for {@link AddToPipelineDialogView}. */
export interface AddToPipelineDialogViewProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Close request handler (backdrop / cancel). */
  readonly onClose: () => void;
  /** Dialog title, e.g. "Add to pipeline". */
  readonly title: string;
  /** Optional subtitle (the player's name). */
  readonly playerName?: string;
  /** Whether to render the board picker (hidden when a board is pre-selected). */
  readonly showBoardPicker: boolean;
  /** Pipeline board options. */
  readonly boards: readonly PipelineBoardOption[];
  /** Whether the board list is loading. */
  readonly boardsLoading: boolean;
  /** Currently selected board id (empty string = none). */
  readonly selectedBoardId: string;
  /** Board selection handler. */
  readonly onBoardChange: (boardId: string) => void;
  /** Stage options for the selected board. */
  readonly stages: readonly PipelineStageOption[];
  /** Whether the stage list is loading. */
  readonly stagesLoading: boolean;
  /** Currently selected stage id (empty string = none). */
  readonly selectedStageId: string;
  /** Stage selection handler. */
  readonly onStageChange: (stageId: string) => void;
  /** Whether an add is in flight. */
  readonly isAdding: boolean;
  /** Whether the confirm button is enabled. */
  readonly canConfirm: boolean;
  /** Confirm handler. */
  readonly onConfirm: () => void;
}

/**
 * Render a single stage label, appending a terminal marker when relevant.
 * @param stage - The stage option.
 * @returns The label string.
 */
const stageLabel = (stage: PipelineStageOption): string =>
  stage.isTerminal ? `${stage.name} (terminal)` : stage.name;

/**
 * Presentation for the pick-pipeline-and-stage dialog.
 * @param props - {@link AddToPipelineDialogViewProps}
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onClose - Close request handler
 * @param props.title - Dialog title
 * @param props.playerName - Optional subtitle (the player's name)
 * @param props.showBoardPicker - Whether to render the board picker
 * @param props.boards - Pipeline board options
 * @param props.boardsLoading - Whether the board list is loading
 * @param props.selectedBoardId - Currently selected board id
 * @param props.onBoardChange - Board selection handler
 * @param props.stages - Stage options for the selected board
 * @param props.stagesLoading - Whether the stage list is loading
 * @param props.selectedStageId - Currently selected stage id
 * @param props.onStageChange - Stage selection handler
 * @param props.isAdding - Whether an add is in flight
 * @param props.canConfirm - Whether the confirm button is enabled
 * @param props.onConfirm - Confirm handler
 * @returns The dialog modal.
 */
const AddToPipelineDialogView = ({
  isOpen,
  onClose,
  title,
  playerName,
  showBoardPicker,
  boards,
  boardsLoading,
  selectedBoardId,
  onBoardChange,
  stages,
  stagesLoading,
  selectedStageId,
  onStageChange,
  isAdding,
  canConfirm,
  onConfirm,
}: AddToPipelineDialogViewProps) => (
  <Modal
    isOpen={isOpen}
    onClose={isAdding ? undefined : onClose}
    closeOnOverlayClick
    size="md"
  >
    <ModalBackdrop />
    <ModalContent testID="add-to-pipeline-dialog">
      <ModalHeader>
        <Stack className="gap-1">
          <Text variant="title" className="text-content-primary">
            {title}
          </Text>
          {playerName ? (
            <Text variant="body" className="text-content-secondary">
              {playerName}
            </Text>
          ) : null}
        </Stack>
      </ModalHeader>
      <ModalBody>
        <Stack className="gap-4 py-2">
          {showBoardPicker ? (
            <Stack className="gap-2">
              <Text variant="body-strong" className="text-content-secondary">
                Pipeline
              </Text>
              <Select
                selectedValue={selectedBoardId}
                onValueChange={onBoardChange}
                isDisabled={boardsLoading || isAdding}
              >
                <SelectTrigger variant="outline" size="md" className="pr-3">
                  <SelectInput
                    testID="add-to-pipeline-board-input"
                    placeholder={
                      boardsLoading ? "Loading pipelines…" : "Choose a pipeline"
                    }
                    className="flex-1"
                  />
                  <SelectIcon as={ChevronDownIcon} />
                </SelectTrigger>
                <SelectPortal>
                  <SelectBackdrop />
                  <SelectContent>
                    <SelectDragIndicatorWrapper>
                      <SelectDragIndicator />
                    </SelectDragIndicatorWrapper>
                    {boards.map(board => (
                      <SelectItem
                        key={board.id}
                        label={board.name}
                        value={board.id}
                      />
                    ))}
                  </SelectContent>
                </SelectPortal>
              </Select>
            </Stack>
          ) : null}

          <Stack className="gap-2">
            <Text variant="body-strong" className="text-content-secondary">
              Stage
            </Text>
            <Select
              selectedValue={selectedStageId}
              onValueChange={onStageChange}
              isDisabled={!selectedBoardId || stagesLoading || isAdding}
            >
              <SelectTrigger variant="outline" size="md" className="pr-3">
                <SelectInput
                  testID="add-to-pipeline-stage-input"
                  placeholder={
                    stagesLoading ? "Loading stages…" : "Choose a stage"
                  }
                  className="flex-1"
                />
                <SelectIcon as={ChevronDownIcon} />
              </SelectTrigger>
              <SelectPortal>
                <SelectBackdrop />
                <SelectContent>
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>
                  {stages.map(stage => (
                    <SelectItem
                      key={stage.id}
                      label={stageLabel(stage)}
                      value={stage.id}
                    />
                  ))}
                </SelectContent>
              </SelectPortal>
            </Select>
          </Stack>

          <Stack
            direction="horizontal"
            className="items-center justify-end gap-3 pt-2"
          >
            <Button
              label="Cancel"
              variant="outline"
              tone="secondary"
              size="md"
              onPress={onClose}
              isDisabled={isAdding}
              accessibilityLabel="Cancel"
            />
            <Button
              testID="add-to-pipeline-confirm"
              label="Add to pipeline"
              variant="solid"
              tone="primary"
              size="md"
              onPress={onConfirm}
              isDisabled={!canConfirm}
              isLoading={isAdding}
              accessibilityLabel="Confirm add to pipeline"
            />
          </Stack>
        </Stack>
      </ModalBody>
    </ModalContent>
  </Modal>
);

AddToPipelineDialogView.displayName = "AddToPipelineDialogView";

export default memo(AddToPipelineDialogView);
